
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TenderMatch } from "@/pages/TenderAgent";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Building2, Clock, HelpCircle, Trash2, MoreHorizontal, Euro, MapPin } from "lucide-react";
import { formatDate } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";
import { getMainDeadline, getAdditionalInfoDeadline, isProcedureOpen } from "@/utils/dateFormatters";
import { getEnglishText } from "../../../supabase/functions/shared/text-utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { getProcedureTypeDescription } from "@/utils/procedureTypeUtils";

export interface TenderCardProps {
  tender: TenderMatch;
  onClick: () => void;
  // Adding the missing properties used in TenderWorkflow and TenderList
  onStatusChange?: (status: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  onSaveTender?: () => Promise<void>; // Added this prop to fix the type error
  viewMode?: string;
}

export const TenderCard = ({
  tender,
  onClick,
  onStatusChange,
  onDelete,
  onSaveTender,
  viewMode = "dashboard"
}: TenderCardProps) => {
  const {
    t,
    i18n
  } = useTranslation("tenderAgent");
  const isCurrentLanguageDanish = i18n.language === "da";
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);

  // Add the missing handleSaveBtnClick function
  const handleSaveBtnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSaveTender) {
      onSaveTender();
    }
  };

  // Extract title from the tender data
  const getTitle = () => {
    if (tender["notice-title"]) {
      return getEnglishText(tender["notice-title"], "Tender " + (tender.publication_number || tender.id || ""));
    }
    if (tender.title) {
      return tender.title;
    }
    return "Tender " + (tender.publication_number || tender.id || "");
  };

  // Extract organization from the tender data - checking multiple possible fields
  const getOrganization = () => {
    // Try buyer-name first with eng language field
    if (tender["buyer-name"]) {
      return getEnglishText(tender["buyer-name"], "Organization not specified");
    }

    // Then try AU field which might contain organization name in TED data
    if (tender.AU) {
      return typeof tender.AU === 'string' ? tender.AU : getEnglishText(tender.AU, "Organization not specified");
    }

    // Then try organization_name which might be set directly
    if (tender.organization_name) {
      return tender.organization_name;
    }

    // If no organization name is found, use a default message
    return "Organization not specified";
  };

  // Get procedure type from the tender data - improved to check more fields
  const getProcedureType = () => {
    let procedureTypeCode = null;

    // Check all possible fields where procedure type might be stored
    if (tender["procedure-type"]) {
      procedureTypeCode = tender["procedure-type"];
    } else if (tender.PR) {
      procedureTypeCode = tender.PR;
    } else if (tender.tender_data && tender.tender_data["procedure-type"]) {
      procedureTypeCode = tender.tender_data["procedure-type"];
    } else if (tender.TOP_TYPE) {
      procedureTypeCode = tender.TOP_TYPE;
    } else if (tender.tenderType) {
      procedureTypeCode = tender.tenderType;
    } else if (tender["BT-105-Procedure"]) {
      procedureTypeCode = tender["BT-105-Procedure"];
    }
    return procedureTypeCode ? getProcedureTypeDescription(procedureTypeCode) : null;
  };

  // Get location from the tender data - making sure to try all possible fields
  const getLocation = () => {
    // Check buyer-country first
    if (tender["buyer-country"]) {
      return tender["buyer-country"];
    }

    // Then check CY field which is common in TED data
    if (tender.CY) {
      return tender.CY;
    }

    // Then check place-of-performance
    if (tender["place-of-performance"]) {
      return tender["place-of-performance"];
    }

    // Then check RC field
    if (tender.RC) {
      return tender.RC;
    }

    // Check place-of-performance-country-lot if it's an array
    if (Array.isArray(tender["place-of-performance-country-lot"]) && tender["place-of-performance-country-lot"].length > 0) {
      return tender["place-of-performance-country-lot"][0];
    }
    return null;
  };

  // Get estimated value from the tender data
  const getValue = () => {
    if (tender["estimated-value-proc"]) {
      const value = tender["estimated-value-proc"];
      const currency = tender["estimated-value-cur-proc"] || "EUR";
      return `${value} ${currency}`;
    }
    if (tender["tender-value"]?.value) {
      return `${tender["tender-value"].value} ${tender["tender-value"].currency || "EUR"}`;
    }
    if (tender.value) {
      return tender.value;
    }
    return null;
  };

  // Use the main deadline (DT) and show label based on procedure type
  const mainDeadlineStr = getMainDeadline(tender);
  const mainDeadline = mainDeadlineStr ? formatDate(mainDeadlineStr, isCurrentLanguageDanish) : isCurrentLanguageDanish ? "Frist ikke angivet" : "Deadline not specified";

  // Use the additional information request deadline (deadline)
  const additionalInfoDeadlineStr = getAdditionalInfoDeadline(tender);
  const additionalInfoDeadline = additionalInfoDeadlineStr ? formatDate(additionalInfoDeadlineStr, isCurrentLanguageDanish) : null;

  // Determine if this is an open or restricted procedure
  const isProcedureOpenType = isProcedureOpen(tender);

  // Set deadline label based on procedure type
  const mainDeadlineLabel = isProcedureOpenType ? isCurrentLanguageDanish ? "Tilbudsfrist" : "Bid deadline" : isCurrentLanguageDanish ? "Ansøgningsfrist" : "Application deadline";

  // Get status label based on status value
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'not_started':
        return isCurrentLanguageDanish ? 'Ikke begyndt' : 'Not started';
      case 'in_progress':
        return isCurrentLanguageDanish ? 'I proces' : 'In progress';
      case 'ready_to_submit':
        return isCurrentLanguageDanish ? 'Klar til indsendelse' : 'Ready to submit';
      case 'submitted':
        return isCurrentLanguageDanish ? 'Indsendt' : 'Submitted';
      case 'won':
        return isCurrentLanguageDanish ? 'Vundet' : 'Won';
      case 'lost':
        return isCurrentLanguageDanish ? 'Tabt' : 'Lost';
      default:
        return isCurrentLanguageDanish ? 'Ikke begyndt' : 'Not started';
    }
  };

  // Get status color based on status value
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'ready_to_submit':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'submitted':
        return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
      case 'won':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'lost':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  // Get status from tender data
  const status = tender.status || 'not_started';
  const procedureType = getProcedureType();
  const location = getLocation();
  const value = getValue();
  return <Card className="hover:bg-gray-50 cursor-pointer transition-colors">
      <CardContent className="p-4 space-y-3">
        <div className="flex justify-between">
          <div className="flex-1" onClick={onClick}>
            <h3 className="font-medium line-clamp-2">{getTitle()}</h3>
            <div className="flex items-center text-gray-500 text-sm mt-1">
              <Building2 className="h-3.5 w-3.5 mr-1" />
              <span className="truncate">{getOrganization()}</span>
            </div>
          </div>
          
          {viewMode === "saved" && <div className="flex items-start space-x-2 ml-4">
              <Badge className={getStatusColor(status)}>
                {getStatusLabel(status)}
              </Badge>
              
              <DropdownMenu open={isStatusMenuOpen} onOpenChange={setIsStatusMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px] bg-white shadow-md z-50 border border-gray-200">
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('not_started')}>
                    {isCurrentLanguageDanish ? 'Ikke begyndt' : 'Not started'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('in_progress')}>
                    {isCurrentLanguageDanish ? 'I proces' : 'In progress'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('ready_to_submit')}>
                    {isCurrentLanguageDanish ? 'Klar til indsendelse' : 'Ready to submit'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('submitted')}>
                    {isCurrentLanguageDanish ? 'Indsendt' : 'Submitted'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('won')}>
                    {isCurrentLanguageDanish ? 'Vundet' : 'Won'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onStatusChange && onStatusChange('lost')}>
                    {isCurrentLanguageDanish ? 'Tabt' : 'Lost'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDelete && onDelete()} className="text-red-600 focus:text-red-600">
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isCurrentLanguageDanish ? 'Slet' : 'Delete'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>}
          
          {/* Add Save button for agent view */}
          {viewMode === "agent" && onSaveTender && <Button variant="outline" size="sm" onClick={handleSaveBtnClick}>
              {isCurrentLanguageDanish ? 'Gem' : 'Save'}
            </Button>}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2" onClick={onClick}>
          <div className="flex items-center text-sm">
            <Clock className="h-3.5 w-3.5 mr-1 text-primary" />
            <span className="font-medium mr-1">{mainDeadlineLabel}:</span>
            <span>{mainDeadline}</span>
          </div>

          {additionalInfoDeadline && <div className="flex items-center text-sm sm:ml-4">
              <HelpCircle className="h-3.5 w-3.5 mr-1 text-primary" />
              <span className="font-medium mr-1">{isCurrentLanguageDanish ? "Frist for oplysninger:" : "Info deadline:"}</span>
              <span>{additionalInfoDeadline}</span>
            </div>}
        </div>
        
        <div className="flex flex-wrap items-center gap-2 mt-2" onClick={onClick}>
          {procedureType && <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {procedureType}
            </Badge>}
          
          {value && <div className="flex items-center">
              <Euro className="h-3.5 w-3.5 mr-1 text-gray-500" />
              <span className="text-sm text-gray-700">{value}</span>
            </div>}
            
          {location && <div className="flex items-center">
              <MapPin className="h-3.5 w-3.5 mr-1 text-gray-500" />
              <span className="text-sm text-gray-700">{location}</span>
            </div>}
        </div>
      </CardContent>
    </Card>;
};
